import { useAppDispatch, useAppSelector } from "app/hooks";
import { useCallback, useEffect, useMemo } from "react";
import { goToNextStep, goToPreviousStep, setAnswer } from "../../slices/genericProfilingSlice";
import { selectAnswers, selectCurrentQuestionAnswered, selectCurrentQuestionGroup, selectCurrentStep, selectEligibilityResult, selectExternalAnswers, selectIsFirstStep, selectIsLastStep, selectProfilingConfig, selectVisibleSteps } from "../../slices/genericSelectors";
import { PossibleAnswer } from "../../types/interfaces";

export const useCBNOProfiling = (onSubmit: (answers: Record<string, any>) => void) => {
  const dispatch = useAppDispatch();

  const answers = useAppSelector(selectAnswers);
  const externalAnswers = useAppSelector(selectExternalAnswers);
  const currentStep = useAppSelector(selectCurrentStep);
  const visibleSteps = useAppSelector(selectVisibleSteps);
  const currentQuestionGroup = useAppSelector(selectCurrentQuestionGroup);
  const isFirstStep = useAppSelector(selectIsFirstStep);
  const isLastStep = useAppSelector(selectIsLastStep);
  const isCurrentQuestionAnswered = useAppSelector(selectCurrentQuestionAnswered);
  const eligibilityResult = useAppSelector(selectEligibilityResult);
  const config = useAppSelector(selectProfilingConfig);

  // Debug des changements
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Hook CBNO - État mis à jour:', {
        currentStep,
        visibleSteps: visibleSteps.map(s => `${s.step}:${s.title}`),
        answers,
        constraintsEnabled: config.constraintsEnabled
      });
    }
  }, [currentStep, visibleSteps, answers, config.constraintsEnabled]);

  // Questions à afficher (uniquement celles du groupe courant)
  const questionsToShow = useMemo(() => {
    const questions = currentQuestionGroup?.questions || [];
    return questions;
  }, [currentQuestionGroup]);

  // Gestion des réponses avec debug
  const handleQuestionAnswer = useCallback((questionKey: string, answer: PossibleAnswer[]) => {
    dispatch(setAnswer({ questionKey, value: answer }));
  }, [dispatch]);

  // Navigation avec debug
  const handleNext = useCallback(() => {
    if (isLastStep && isCurrentQuestionAnswered) {
      // Convertir les réponses au format attendu
      const formattedAnswers: Record<string, any> = {};
      Object.entries(answers).forEach(([key, values]) => {
        formattedAnswers[key] = values && values.length > 0 ? values[0] : null;
      });

      onSubmit(formattedAnswers);
    } else if (isCurrentQuestionAnswered) {
      dispatch(goToNextStep());
    } else {
    }
  }, [dispatch, isLastStep, isCurrentQuestionAnswered, answers, onSubmit]);

  const handlePrevious = useCallback(() => {
      dispatch(goToPreviousStep());
  }, [dispatch]);

  // Step « passable » (toutes ses questions facultatives) : on avance sans exiger de
  // réponse ; sur le dernier step, on soumet avec les réponses données jusqu'ici.
  const isCurrentStepSkippable = useMemo(
    () => questionsToShow.length > 0 && questionsToShow.every(q => q.isRequired === false),
    [questionsToShow]
  );

  const handleSkip = useCallback(() => {
    if (!isCurrentStepSkippable) return;

    if (isLastStep) {
      const formattedAnswers: Record<string, any> = {};
      Object.entries(answers).forEach(([key, values]) => {
        formattedAnswers[key] = values && values.length > 0 ? values[0] : null;
      });
      onSubmit(formattedAnswers);
    } else {
      dispatch(goToNextStep());
    }
  }, [dispatch, isCurrentStepSkippable, isLastStep, answers, onSubmit]);

  // Informations sur le step courant
  const currentStepInfo = useMemo(() => {
    const stepIndex = visibleSteps.findIndex(s => s.step === currentStep);
    const info = {
      current: stepIndex + 1,
      total: visibleSteps.length,
      percentage: visibleSteps.length > 0 ? ((stepIndex + 1) / visibleSteps.length) * 100 : 0
    };
    return info;
  }, [visibleSteps, currentStep]);

  // Helper pour vérifier si une question a une réponse externe
  const isQuestionExternal = useCallback((questionKey: string): boolean => {
    return externalAnswers ? questionKey in externalAnswers : false;
  }, [externalAnswers]);

  return {
    // État
    answers,
    externalAnswers,
    currentStep,
    visibleSteps,
    currentQuestionGroup,
    questionsToShow,
    eligibilityResult,

    // Flags
    isFirstStep,
    isLastStep,
    isCurrentQuestionAnswered,
    isCurrentStepSkippable,

    // Actions
    handleQuestionAnswer,
    handleNext,
    handlePrevious,
    handleSkip,

    // Helpers
    isQuestionExternal,

    // Infos
    currentStepInfo
  };
};
